import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Banknote,
  CheckCircle2,
  CreditCard,
  Loader2,
  ShoppingBag,
} from "lucide-react";
import { useAuth } from "../../auth/AuthContext";
import {
  clearPosDisplayState,
  PosDisplayState,
  readPosDisplayState,
  rememberPosDisplayBounds,
  subscribePosDisplayState,
} from "../../lib/posDisplaySync";

const PANEL = "rounded-3xl bg-white/10 border border-white/15 backdrop-blur-sm";

const CustomerDisplay = () => {
  const { user } = useAuth();
  const [state, setState] = useState<PosDisplayState>(() => readPosDisplayState());
  const [now, setNow] = useState(() => new Date());

  const branding = useMemo(() => {
    const company = user?.company;
    return {
      companyName: company?.company_name || state.companyName || "Welcome",
      currencySymbol:
        company?.currency_symbol ||
        company?.currency?.currency_symbol ||
        state.currencySymbol ||
        "",
    };
  }, [user, state.companyName, state.currencySymbol]);

  const money = (value?: number | null) =>
    `${branding.currencySymbol}${(value || 0).toFixed(2)}`;

  useEffect(() => {
    const unsubscribe = subscribePosDisplayState(setState);

    const onMessage = (event: MessageEvent) => {
      if (event?.data?.type === "close") {
        window.close();
      }
    };

    let channel: BroadcastChannel | null = null;
    try {
      channel = new BroadcastChannel("glossa-pos-display");
      channel.addEventListener("message", onMessage);
    } catch {
      // ignore
    }

    const persistBounds = () => rememberPosDisplayBounds(window);
    window.addEventListener("beforeunload", persistBounds);
    window.addEventListener("resize", persistBounds);

    return () => {
      unsubscribe();
      channel?.removeEventListener("message", onMessage);
      channel?.close();
      window.removeEventListener("beforeunload", persistBounds);
      window.removeEventListener("resize", persistBounds);
    };
  }, []);

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 30000);
    return () => window.clearInterval(id);
  }, []);

  // If this window is opened alone with no state, show branded idle
  useEffect(() => {
    if (!state.updatedAt && branding.companyName) {
      clearPosDisplayState({
        companyName: branding.companyName,
        currencySymbol: branding.currencySymbol,
      });
    }
  }, [branding.companyName, branding.currencySymbol, state.updatedAt]);

  const hasItems = state.items.length > 0;
  const status = state.paymentStatus || "idle";
  const isIdle = status === "idle" && !hasItems;
  const isFullScreenStatus =
    status === "card" || status === "saving" || status === "complete";
  const itemCount = state.items.reduce((sum, item) => sum + item.quantity, 0);
  const amountDue = state.amountDue ?? state.total;

  return (
    <div className="h-screen w-full bg-gradient-to-br from-[#2A1B45] via-[#402B69] to-[#885ABB] text-white flex flex-col overflow-hidden">
      <header className="px-8 sm:px-12 pt-8 pb-5 flex items-start justify-between gap-6">
        <div className="min-w-0">
          <p className="text-3xl sm:text-5xl font-bold tracking-tight truncate">
            {branding.companyName}
          </p>
          <p className="text-white/50 text-xs sm:text-sm uppercase tracking-[0.3em] mt-2">
            Customer display
          </p>
        </div>
        <div className="text-right shrink-0">
          {state.customerName && !isIdle ? (
            <>
              <p className="text-white/50 text-xs uppercase tracking-wide">
                Customer
              </p>
              <p className="text-lg sm:text-2xl font-semibold">
                {state.customerName}
              </p>
            </>
          ) : (
            <p className="text-2xl sm:text-3xl font-semibold text-white/70">
              {now.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </p>
          )}
        </div>
      </header>

      <main className="flex-1 px-6 sm:px-12 pb-6 min-h-0 overflow-hidden">
        {isIdle ? (
          <IdlePanel companyName={branding.companyName} />
        ) : isFullScreenStatus ? (
          <AnimatePresence mode="wait">
            <motion.div
              key={status}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.25 }}
              className="h-full"
            >
              {status === "card" ? (
                <CardTenderPanel
                  amount={amountDue || state.total}
                  money={money}
                  tenderLabel={state.tenderLabel}
                />
              ) : status === "saving" ? (
                <SavingPanel
                  money={money}
                  total={state.total}
                  amountPaid={
                    state.amountPaid ?? state.totalTendered ?? state.total
                  }
                  changeDue={state.changeDue}
                />
              ) : (
                <CompletePanel
                  money={money}
                  amountPaid={
                    state.amountPaid ?? state.totalTendered ?? state.total
                  }
                  changeDue={state.changeDue}
                />
              )}
            </motion.div>
          </AnimatePresence>
        ) : (
          <div className="h-full grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-6">
            <OrderPanel
              items={state.items}
              itemCount={itemCount}
              money={money}
              subtotal={state.originalNet}
              discount={state.discount}
              tax={state.tax}
              total={state.total}
            />

            <div className="min-h-0">
              <AnimatePresence mode="wait">
                <motion.div
                  key={status}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -16 }}
                  transition={{ duration: 0.25 }}
                  className="h-full"
                >
                  {status === "tendered" ? (
                    <TenderedPanel
                      money={money}
                      tenderLabel={state.tenderLabel}
                      totalTendered={state.totalTendered}
                      amountDue={amountDue}
                      changeDue={state.changeDue}
                    />
                  ) : (
                    <TotalPanel
                      money={money}
                      total={state.total}
                      amountDue={amountDue}
                      awaiting={status === "awaiting"}
                    />
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

const IdlePanel = ({ companyName }: { companyName: string }) => (
  <div
    className={`${PANEL} h-full flex flex-col items-center justify-center text-center px-8`}
  >
    <p className="text-4xl sm:text-5xl font-bold mb-5">Welcome</p>
    <p className="text-lg sm:text-2xl text-white/70 max-w-3xl">
      Thanks for visiting {companyName}
    </p>
    <p className="text-white/40 text-sm sm:text-base mt-6">
      Your order will appear here
    </p>
  </div>
);

type MoneyFn = (value?: number | null) => string;

const OrderPanel = ({
  items,
  itemCount,
  money,
  subtotal,
  discount,
  tax,
  total,
}: {
  items: PosDisplayState["items"];
  itemCount: number;
  money: MoneyFn;
  subtotal: number;
  discount: number;
  tax: number;
  total: number;
}) => {
  return (
  <div className={`${PANEL} p-6 sm:p-8 flex flex-col min-h-0 overflow-hidden`}>
    <div className="flex items-center justify-between gap-4 mb-5">
      <h2 className="text-2xl sm:text-3xl font-semibold flex items-center gap-3">
        <ShoppingBag className="w-6 h-6 text-white/70" />
        Your order
      </h2>
      <span className="text-sm sm:text-base text-white/60">
        {itemCount} {itemCount === 1 ? "item" : "items"}
      </span>
    </div>

    <div className="flex-1 overflow-y-auto space-y-3 pr-2 min-h-0">
      {items.length === 0 && (
        <p className="text-white/50 text-lg">Waiting for items…</p>
      )}
      <AnimatePresence initial={false}>
        {items.map((item) => (
          <motion.div
            key={`${item.kind}-${item.id}`}
            layout
            initial={{ opacity: 0, x: -12 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 12 }}
            transition={{ duration: 0.2 }}
            className="flex items-start justify-between gap-4 rounded-2xl bg-white/10 px-4 py-3"
          >
            <div className="min-w-0">
              <p className="text-lg sm:text-xl font-semibold truncate">
                {item.name}
              </p>
              <p className="text-white/60 text-sm sm:text-base">
                {item.quantity} × {money(item.unitPrice)}
              </p>
            </div>
            <p className="text-lg sm:text-xl font-bold whitespace-nowrap">
              {money(item.lineTotal)}
            </p>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>

    <div className="mt-6 pt-5 border-t border-white/15 space-y-2 text-base sm:text-lg">
      <div className="flex justify-between text-white/70">
        <span>Subtotal</span>
        <span className="font-semibold text-white">{money(subtotal)}</span>
      </div>
      {discount > 0 && (
        <div className="flex justify-between text-emerald-300">
          <span>Discount</span>
          <span className="font-semibold">-{money(discount)}</span>
        </div>
      )}
      <div className="flex justify-between text-white/70">
        <span>Tax</span>
        <span className="font-semibold text-white">{money(tax)}</span>
      </div>
      <div className="flex justify-between pt-2 text-xl sm:text-2xl font-bold">
        <span>Total</span>
        <span>{money(total)}</span>
      </div>
    </div>
  </div>
  );
};

const StatusCard = ({ children }: { children: React.ReactNode }) => (
  <div className="h-full rounded-3xl bg-white text-[#1E1E1E] p-8 sm:p-10 shadow-2xl flex flex-col items-center justify-center text-center overflow-hidden">
    {children}
  </div>
);

const TotalPanel = ({
  money,
  total,
  amountDue,
  awaiting,
}: {
  money: MoneyFn;
  total: number;
  amountDue?: number | null;
  awaiting: boolean;
}) => (
  <StatusCard>
    <p className="text-gray-500 text-sm sm:text-base uppercase tracking-[0.2em] mb-3">
      {awaiting ? "Amount to pay" : "Order total"}
    </p>
    <p className="text-6xl sm:text-8xl font-bold text-[#402B69] leading-none">
      {money(awaiting ? (amountDue ?? total) : total)}
    </p>
    {awaiting ? (
      <>
        <p className="text-gray-500 text-lg sm:text-2xl mt-8">
          Ready whenever you are
        </p>
        <div className="flex items-center gap-4 mt-6">
          <span className="flex items-center gap-2 rounded-full bg-[#F3EEFB] text-[#402B69] px-5 py-2.5 text-base sm:text-lg font-semibold">
            <Banknote className="w-5 h-5" /> Cash
          </span>
          <span className="flex items-center gap-2 rounded-full bg-[#F3EEFB] text-[#402B69] px-5 py-2.5 text-base sm:text-lg font-semibold">
            <CreditCard className="w-5 h-5" /> Card
          </span>
        </div>
      </>
    ) : (
      <p className="text-gray-500 text-lg sm:text-2xl mt-8">
        Please check your items
      </p>
    )}
  </StatusCard>
);

const CardTenderPanel = ({
  amount,
  money,
  tenderLabel,
}: {
  amount: number;
  money: MoneyFn;
  tenderLabel?: string | null;
}) => (
  <StatusCard>
    <motion.div
      animate={{ scale: [1, 1.06, 1] }}
      transition={{ duration: 1.8, repeat: Infinity }}
      className="w-24 h-24 sm:w-28 sm:h-28 rounded-3xl bg-[#402B69] flex items-center justify-center mb-8 shadow-lg"
    >
      <CreditCard className="w-12 h-12 sm:w-14 sm:h-14 text-white" />
    </motion.div>
    <p className="text-3xl sm:text-5xl font-bold text-[#1E1E1E] mb-3">
      Insert, tap or swipe
    </p>
    <p className="text-gray-500 text-lg sm:text-2xl mb-8">
      Follow the prompts on the card machine
    </p>
    <p className="text-gray-500 text-sm uppercase tracking-[0.2em] mb-2">
      {tenderLabel || "Card"} payment
    </p>
    <p className="text-5xl sm:text-7xl font-bold text-[#402B69] leading-none">
      {money(amount)}
    </p>
    <div className="flex items-center gap-2 mt-8 text-[#885ABB]">
      <Loader2 className="w-5 h-5 animate-spin" />
      <span className="text-base sm:text-lg font-medium">
        Waiting for the terminal…
      </span>
    </div>
  </StatusCard>
);

const TenderedPanel = ({
  money,
  tenderLabel,
  totalTendered,
  amountDue,
  changeDue,
}: {
  money: MoneyFn;
  tenderLabel?: string | null;
  totalTendered?: number | null;
  amountDue?: number | null;
  changeDue?: number | null;
}) => {
  const remaining = amountDue ?? 0;
  const change = changeDue ?? 0;
  const isCash = (tenderLabel || "").toLowerCase().includes("cash");

  return (
    <StatusCard>
      <div className="w-20 h-20 rounded-3xl bg-[#F3EEFB] flex items-center justify-center mb-7">
        {isCash ? (
          <Banknote className="w-10 h-10 text-[#402B69]" />
        ) : (
          <CreditCard className="w-10 h-10 text-[#402B69]" />
        )}
      </div>
      <p className="text-gray-500 text-sm uppercase tracking-[0.2em] mb-2">
        {tenderLabel || "Payment"} received
      </p>
      <p className="text-5xl sm:text-7xl font-bold text-[#402B69] leading-none">
        {money(totalTendered)}
      </p>

      {remaining > 0.001 ? (
        <div className="mt-9 w-full rounded-2xl bg-[#FFF4E5] px-6 py-5">
          <p className="text-[#8A5A00] text-sm uppercase tracking-[0.2em] mb-1">
            Still to pay
          </p>
          <p className="text-3xl sm:text-4xl font-bold text-[#8A5A00]">
            {money(remaining)}
          </p>
        </div>
      ) : (
        <div className="mt-9 w-full">
          <PaidChangeRows
            money={money}
            amountPaid={totalTendered}
            changeDue={change}
          />
        </div>
      )}
    </StatusCard>
  );
};

const PaidChangeRows = ({
  money,
  amountPaid,
  changeDue,
}: {
  money: MoneyFn;
  amountPaid?: number | null;
  changeDue?: number | null;
}) => (
  <div className="w-full max-w-xl space-y-3">
    <div className="flex items-center justify-between rounded-2xl bg-[#F3EEFB] px-6 py-5">
      <span className="text-gray-600 text-xl sm:text-2xl">Paid</span>
      <span className="text-3xl sm:text-4xl font-bold text-[#402B69]">
        {money(amountPaid)}
      </span>
    </div>
    <div className="flex items-center justify-between rounded-2xl bg-[#E8F7EE] px-6 py-5">
      <span className="text-[#1E7A45] text-xl sm:text-2xl">Change</span>
      <span className="text-3xl sm:text-4xl font-bold text-[#1E7A45]">
        {money(changeDue ?? 0)}
      </span>
    </div>
  </div>
);

const SavingPanel = ({
  money,
  total,
  amountPaid,
  changeDue,
}: {
  money: MoneyFn;
  total: number;
  amountPaid?: number | null;
  changeDue?: number | null;
}) => (
    <StatusCard>
      <div className="relative w-24 h-24 mb-6">
        <div className="absolute inset-0 rounded-full border-4 border-[#F3EEFB]" />
        <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-[#885ABB] animate-spin" />
        <div className="absolute inset-0 flex items-center justify-center">
          <ShoppingBag className="w-9 h-9 text-[#402B69]" />
        </div>
      </div>
      <p className="text-3xl sm:text-5xl font-bold mb-2">Processing payment</p>
      <p className="text-gray-500 text-lg sm:text-xl mb-8">
        Please wait while we complete your order
      </p>

      <PaidChangeRows
        money={money}
        amountPaid={amountPaid ?? total}
        changeDue={changeDue}
      />
    </StatusCard>
);

const CompletePanel = ({
  money,
  amountPaid,
  changeDue,
}: {
  money: MoneyFn;
  amountPaid?: number | null;
  changeDue?: number | null;
}) => (
  <StatusCard>
    <motion.div
      initial={{ scale: 0.6, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: "spring", stiffness: 220, damping: 16 }}
      className="w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-[#E8F7EE] flex items-center justify-center mb-8"
    >
      <CheckCircle2 className="w-14 h-14 text-[#1E7A45]" />
    </motion.div>
    <p className="text-4xl sm:text-6xl font-bold mb-3">Thank you</p>
    <p className="text-gray-500 text-lg sm:text-2xl mb-9">Payment complete</p>

    <PaidChangeRows money={money} amountPaid={amountPaid} changeDue={changeDue} />

    <p className="text-gray-400 text-base sm:text-lg mt-9">
      Have a lovely day
    </p>
  </StatusCard>
);

export default CustomerDisplay;
